import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pickInferenceClient } from './client.js';
import { observeClient } from './observe-client.js';

let idSeq = 0;
const nextId = () => `m${++idSeq}`;
const newConversationId = () =>
  `conv-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
// F1's Mirror embeds F3's meter radar and additionally fetches analyzeMeter — see runAnalysis.
const FEATURE_METHODS = { F1: 'analyzeMirror', F3: 'analyzeMeter', F4: 'analyzeMessageFindings' };

// Human-like reply pacing. Each valid reply bubble has a "thinking" pause
// (typing indicator OFF), then a length-proportional "typing" phase (indicator
// ON), then the bubble appears. The backend may override either phase per
// message via thinkingMs / typingMs on the ChatMessage.
const REPLY_THINK_MIN_MS = 150;
const REPLY_THINK_MAX_MS = 500;
const REPLY_MS_PER_CHAR = 120;
const REPLY_TYPE_MIN_MS = 900;
const REPLY_TYPE_MAX_MS = 7000;
const REPLY_TYPE_JITTER_MS = 600;

const clampMs = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const randBetween = (lo, hi) => lo + Math.random() * (hi - lo);

/**
 * Default pacing for one reply bubble. Honors explicit backend overrides
 * (thinkingMs / typingMs); otherwise derives a random thinking pause and a
 * length-proportional typing duration with jitter.
 *
 * @param {{ text?: string, thinkingMs?: number, typingMs?: number }} message
 * @returns {{ thinkingMs: number, typingMs: number }}
 */
export function defaultReplyTiming(message) {
  const text = message?.text ?? '';
  const thinkingMs =
    typeof message?.thinkingMs === 'number'
      ? message.thinkingMs
      : Math.round(randBetween(REPLY_THINK_MIN_MS, REPLY_THINK_MAX_MS));
  const typingMs =
    typeof message?.typingMs === 'number'
      ? message.typingMs
      : Math.round(
          clampMs(text.length * REPLY_MS_PER_CHAR, REPLY_TYPE_MIN_MS, REPLY_TYPE_MAX_MS) +
            Math.random() * REPLY_TYPE_JITTER_MS
        );
  return { thinkingMs, typingMs };
}

export function useInference(opts = {}) {
  const onInferenceLogRef = useRef(opts.onInferenceLog);
  const onSendErrorRef = useRef(opts.onSendError);
  const onMessageSentRef = useRef(opts.onMessageSent);
  const onDraftScoreRef = useRef(opts.onDraftScore);
  onDraftScoreRef.current = opts.onDraftScore;
  onInferenceLogRef.current = opts.onInferenceLog;
  onSendErrorRef.current = opts.onSendError;
  onMessageSentRef.current = opts.onMessageSent;
  const enabledFeaturesRef = useRef(opts.enabledFeatures ?? []);
  enabledFeaturesRef.current = opts.enabledFeatures ?? [];
  // analyzeOnSend=false decouples background analysis (F1/F3/F4) from every
  // send — used by the report-experiment report gate, which instead
  // triggers refreshAnalysis() on its own interval. F4's unprocessedF4Ref
  // bookkeeping and message_sent telemetry stay unconditional either way
  // (see appendSentMessage) so the next refreshAnalysis() still has pending
  // ids to work with and Stream B stays comparable across arms.
  const analyzeOnSendRef = useRef(opts.analyzeOnSend ?? true);
  analyzeOnSendRef.current = opts.analyzeOnSend ?? true;

  const defaultClient = useMemo(() => {
    const client =
      opts.client ??
      pickInferenceClient(typeof window === 'undefined' ? '' : window.location.search, {
        sessionToken: opts.sessionToken ?? null,
        draftMode: opts.draftMode ?? 'provider',
        appConfig: opts.appConfig,
      });
    return observeClient(client, (row) => onInferenceLogRef.current?.(row));
  }, [opts.appConfig, opts.client, opts.draftMode, opts.sessionToken]);

  const [conversationId] = useState(newConversationId);
  const [history, setHistory] = useState(() => opts.initialHistory ?? []);
  const [latestMeterUpdate, setLatestMeterUpdate] = useState([]);
  const [latestMirror, setLatestMirror] = useState(null);
  const [axisEvidence, setAxisEvidence] = useState({});
  const [analysisUpdating, setAnalysisUpdating] = useState({ F1: false, F3: false, F4: false });
  const [analysisSettled, setAnalysisSettled] = useState({ F1: false, F3: false, F4: false });
  const [analysisErrors, setAnalysisErrors] = useState({});
  const [draftAssessment, setDraftAssessment] = useState(null);
  const [draftScoring, setDraftScoring] = useState({ text: null, status: 'idle' });
  const [inferenceTriggerTimes, setInferenceTriggerTimes] = useState({
    F1: null,
    F3: null,
    F4: null,
  });
  // Whether the 'them' party is currently "typing" (drives the chat typing
  // indicator). True only during a reply's typing phase, not its thinking pause.
  const [replyTyping, setReplyTyping] = useState(false);
  const historyRef = useRef(history);
  historyRef.current = history;
  const computeReplyTimingRef = useRef(opts.computeReplyTiming ?? defaultReplyTiming);
  computeReplyTimingRef.current = opts.computeReplyTiming ?? defaultReplyTiming;
  // Pacing timers retain their resolvers: cancellation must wake the loop,
  // not leave an unresolved sleep holding the single-flight reply lock.
  const replyTimersRef = useRef(new Map());
  // Progress belongs to the currently undelivered bubble. Only actual time
  // spent in its thinking sleep is carried across superseding sends.
  const replyThinkingRef = useRef(null);
  const replyMountedRef = useRef(true);
  const replyVersionRef = useRef(0);
  // Covers both the backend request and pacing. Requests settle before the
  // replacement dispatches; superseded pacing is stopped immediately.
  const replyInFlightRef = useRef(false);
  const cancelReplyPacing = useCallback(() => {
    for (const [id, resolve] of replyTimersRef.current) {
      clearTimeout(id);
      resolve();
    }
    replyTimersRef.current.clear();
  }, []);
  // Reply scheduling preserves one initial thinking deadline while coalescing
  // each pending send burst through a quiet window capped from its first send.
  const replyDebounceTimerRef = useRef(null);
  const replyMaxWaitTimerRef = useRef(null);
  const replyThinkReadyAtRef = useRef(null);
  const replyFirstPendingAtRef = useRef(null);
  const replyLastPendingAtRef = useRef(null);
  // A matured debounce queues one latest-context dispatch while the backend
  // request is still outstanding. Undelivered stale bubbles are never drained.
  const replyRetryPendingRef = useRef(false);
  const clearReplyScheduleTimers = useCallback(() => {
    if (replyDebounceTimerRef.current !== null) {
      clearTimeout(replyDebounceTimerRef.current);
      replyDebounceTimerRef.current = null;
    }
    if (replyMaxWaitTimerRef.current !== null) {
      clearTimeout(replyMaxWaitTimerRef.current);
      replyMaxWaitTimerRef.current = null;
    }
  }, []);
  // Successful targets stay protected against duplicate reply triggers.
  // Failed or superseded requests do not mark a target as answered.
  const lastRepliedMessageIdRef = useRef(null);
  const draftTimerRef = useRef(null);
  const draftVersionRef = useRef(0);
  const draftScoreSeqRef = useRef(0);
  const queuedDraftRef = useRef(null);
  const draftMountedRef = useRef(true);
  const beginDraftScore = useCallback(
    (trigger, attemptId) => {
      const scoring = {
        scoreId: `${conversationId}-score-${++draftScoreSeqRef.current}`,
        trigger,
        ...(attemptId ? { attemptId } : {}),
      };
      onDraftScoreRef.current?.('requested', scoring);
      return scoring;
    },
    [conversationId]
  );
  const settleDraftScore = useCallback((scoring, disposition, detail = {}) => {
    onDraftScoreRef.current?.('settled', { ...scoring, disposition, ...detail });
  }, []);
  const schedulerRef = useRef({});
  const unprocessedF4Ref = useRef(new Set());
  // Dispatch order follows the append-only transcript. Queued intent does not
  // invalidate a completion; only a newer accepted endpoint snapshot does.
  const analysisSequenceRef = useRef(0);
  const acceptedAnalysisRef = useRef({ F1: 0, F3: 0, F4: 0 });
  const settledAnalysisRef = useRef({ F1: 0, F3: 0, F4: 0 });
  const dispatchedAnalysisRef = useRef({ F1: 0, F3: 0, F4: 0 });
  const activeAnalysisRef = useRef({ F1: 0, F3: 0, F4: 0 });
  const analysisWaitersRef = useRef(new Set());
  const analysisMountedRef = useRef(true);
  const debounceMs = opts.debounceMs ?? 250;
  const analysisDebounceMs = opts.analysisDebounceMs ?? 500;
  const analysisMaxWaitMs = opts.analysisMaxWaitMs ?? 2000;
  const replyDebounceMs = opts.replyDebounceMs ?? 1200;
  const replyMaxWaitMs = opts.replyMaxWaitMs ?? 4000;
  const replyQuietMs = opts.replyQuietMs ?? 300;

  useEffect(() => {
    analysisMountedRef.current = true;
    const waiters = analysisWaitersRef.current;
    return () => {
      analysisMountedRef.current = false;
      for (const pending of Object.values(schedulerRef.current)) {
        clearTimeout(pending.timer);
        clearTimeout(pending.maxTimer);
        for (const resolve of pending.resolvers) resolve(null);
      }
      schedulerRef.current = {};
      for (const resolve of waiters) resolve(null);
      waiters.clear();
    };
  }, []);

  const transcript = useCallback(
    () => historyRef.current.map(({ id, role, text }) => ({ id, role, text })),
    []
  );
  const emitMessageSent = useCallback(
    (message) => {
      onMessageSentRef.current?.({
        conversationId,
        messageId: message.id,
        message: { id: message.id, role: message.role, text: message.text },
      });
    },
    [conversationId]
  );

  const startAnalysis = useCallback((feature, version) => {
    dispatchedAnalysisRef.current[feature] = version;
    activeAnalysisRef.current[feature] += 1;
    setAnalysisUpdating((current) => ({ ...current, [feature]: true }));
    setAnalysisErrors((current) => ({ ...current, [feature]: null }));
  }, []);

  const finishAnalysis = useCallback((feature) => {
    activeAnalysisRef.current[feature] -= 1;
    const updating = activeAnalysisRef.current[feature] > 0 || !!schedulerRef.current[feature];
    setAnalysisUpdating((current) => ({ ...current, [feature]: updating }));
  }, []);

  const settleAnalysis = useCallback((feature, version, error = null) => {
    if (version < settledAnalysisRef.current[feature]) return;
    settledAnalysisRef.current[feature] = version;
    setAnalysisErrors((current) => ({ ...current, [feature]: error }));
    setAnalysisSettled((current) => ({ ...current, [feature]: true }));
  }, []);

  const acceptAnalysis = useCallback(
    (feature, version) => {
      if (!analysisMountedRef.current) return false;
      if (feature !== 'F4' && version < acceptedAnalysisRef.current[feature]) return false;
      acceptedAnalysisRef.current[feature] = Math.max(
        acceptedAnalysisRef.current[feature],
        version
      );
      settleAnalysis(feature, version);
      setInferenceTriggerTimes((current) => ({ ...current, [feature]: Date.now() }));
      return true;
    },
    [settleAnalysis]
  );

  const runAnalysis = useCallback(
    (feature) => {
      const pending = schedulerRef.current[feature];
      if (!pending) return Promise.resolve();
      clearTimeout(pending.timer);
      clearTimeout(pending.maxTimer);
      delete schedulerRef.current[feature];
      const version = ++analysisSequenceRef.current;
      const messages = transcript();
      const method = FEATURE_METHODS[feature];
      if (typeof defaultClient[method] !== 'function') return Promise.resolve();
      const payload = { conversationId, messages };
      if (feature === 'F4') {
        payload.targetMessageIds = [...unprocessedF4Ref.current];
        unprocessedF4Ref.current.clear();
        if (!payload.targetMessageIds.length) {
          setAnalysisUpdating((current) => ({
            ...current,
            F4: activeAnalysisRef.current.F4 > 0,
          }));
          if (!activeAnalysisRef.current.F4) {
            setAnalysisSettled((current) => ({ ...current, F4: true }));
          }
          return Promise.resolve();
        }
      }
      const alsoFetchMeter =
        feature === 'F1' &&
        !enabledFeaturesRef.current.includes('F3') &&
        typeof defaultClient.analyzeMeter === 'function';
      startAnalysis(feature, version);
      // Promise wrapping also handles synchronous client failures.
      const analysisRequest = Promise.resolve().then(() => defaultClient[method](payload));
      if (alsoFetchMeter) {
        // Supplemental F1 data settles independently. The report gate awaits
        // Mirror, not this request, but late meter data still belongs to its snapshot.
        startAnalysis('F3', version);
        Promise.resolve()
          .then(() => defaultClient.analyzeMeter(payload))
          .then((response) => {
            if (!acceptAnalysis('F3', version)) return;
            setLatestMeterUpdate(response.meterUpdate);
            setAxisEvidence(response.axisEvidence);
          })
          .catch((error) => {
            if (!analysisMountedRef.current || version < dispatchedAnalysisRef.current.F3) return;
            console.warn('[useInference] Meter analysis failed:', error);
            settleAnalysis('F3', version, error);
          })
          .finally(() => {
            if (analysisMountedRef.current) finishAnalysis('F3');
          });
      }
      let cancelWaiter;
      const cancelled = new Promise((resolve) => {
        cancelWaiter = resolve;
        analysisWaitersRef.current.add(resolve);
      });
      const completed = analysisRequest
        .then((response) => {
          if (!acceptAnalysis(feature, version)) return null;
          if (feature === 'F1') setLatestMirror(response.mirrorAggregate);
          if (feature === 'F3') {
            setLatestMeterUpdate(response.meterUpdate);
            setAxisEvidence(response.axisEvidence);
          }
          if (feature === 'F4') {
            const accepted = new Set(payload.targetMessageIds);
            setHistory((current) =>
              current.map((message) => {
                const row = response.findingsByMessage?.find(
                  (item) => item.messageId === message.id
                );
                return accepted.has(message.id) && row
                  ? { ...message, inferences: row.messageInferences }
                  : message;
              })
            );
          }
          return response;
        })
        .catch((error) => {
          if (!analysisMountedRef.current) return null;
          // Failed F4 batches retain their targets for a later retry, even if
          // other disjoint batches have already settled successfully.
          if (feature === 'F4') {
            for (const id of payload.targetMessageIds) unprocessedF4Ref.current.add(id);
          }
          if (version < dispatchedAnalysisRef.current[feature]) return null;
          console.warn(`[useInference.${feature}] background analysis failed`, error);
          settleAnalysis(feature, version, error);
          return null;
        })
        .finally(() => {
          analysisWaitersRef.current.delete(cancelWaiter);
          if (!analysisMountedRef.current) return;
          finishAnalysis(feature);
          // A send that arrived while an F4 request was in flight starts a new burst.
          if (feature === 'F4' && unprocessedF4Ref.current.size)
            scheduleAnalysisRef.current?.('F4');
        });
      return Promise.race([completed, cancelled]);
    },
    [
      acceptAnalysis,
      conversationId,
      defaultClient,
      finishAnalysis,
      settleAnalysis,
      startAnalysis,
      transcript,
    ]
  );

  const scheduleAnalysisRef = useRef(null);
  const scheduleAnalysis = useCallback(
    (feature, { immediate = false } = {}) => {
      if (!analysisMountedRef.current) return Promise.resolve(null);
      if (!enabledFeaturesRef.current.includes(feature)) return Promise.resolve();
      if (typeof defaultClient[FEATURE_METHODS[feature]] !== 'function') return Promise.resolve();
      const existing = schedulerRef.current[feature];
      const resolvers = existing?.resolvers ?? [];
      let resolvePromise;
      const promise = new Promise((resolve) => {
        resolvePromise = resolve;
      });
      resolvers.push(resolvePromise);

      if (existing) {
        clearTimeout(existing.timer);
        if (immediate) clearTimeout(existing.maxTimer);
      }
      const firstAt = existing?.firstAt ?? Date.now();

      if (immediate) {
        schedulerRef.current[feature] = {
          firstAt,
          timer: null,
          maxTimer: null,
          resolvers: [],
        };
        return runAnalysis(feature).then((result) => {
          for (const resolve of resolvers) resolve(result);
          return result;
        });
      }

      const execute = () => {
        const pendingResolvers = [...resolvers];
        resolvers.length = 0;
        runAnalysis(feature).then((result) => {
          for (const resolve of pendingResolvers) resolve(result);
        });
      };
      const delay = Math.max(
        0,
        Math.min(analysisDebounceMs, analysisMaxWaitMs - (Date.now() - firstAt))
      );
      const timer = setTimeout(execute, delay);
      const maxTimer =
        existing?.maxTimer ??
        setTimeout(execute, Math.max(0, analysisMaxWaitMs - (Date.now() - firstAt)));
      schedulerRef.current[feature] = { firstAt, timer, maxTimer, resolvers };
      setAnalysisUpdating((current) => ({ ...current, [feature]: true }));
      return promise;
    },
    [analysisDebounceMs, analysisMaxWaitMs, defaultClient, runAnalysis]
  );
  scheduleAnalysisRef.current = scheduleAnalysis;

  // Recompute background analysis for the currently-enabled features over the
  // existing transcript, without a new send. Used at the mid-experiment reveal
  // (M5) so Mirror (F1) / Risk (F3) show data the instant the feature appears
  // rather than empty state until the participant's next send. F4 backfills its
  // own tints from unprocessedF4Ref on the next send, so a bare schedule here is
  // a no-op for it (empty target set) — harmless, and keeps this feature-generic.
  const refreshAnalysis = useCallback(
    (opts = {}) => {
      const promises = [];
      for (const feature of enabledFeaturesRef.current) {
        if (FEATURE_METHODS[feature]) {
          promises.push(scheduleAnalysis(feature, opts));
        }
      }
      return Promise.all(promises);
    },
    [scheduleAnalysis]
  );

  const appendSentMessage = useCallback(
    (text) => {
      const message = { id: nextId(), role: 'me', text, inferences: [] };
      setHistory((current) => [...current, message]);
      // Keep the ref synchronous so a chat reply or a rapid follow-up sees it.
      historyRef.current = [...historyRef.current, message];
      replyVersionRef.current += 1;
      const thinking = replyThinkingRef.current;
      if (thinking && thinking.startedAt !== null) {
        const elapsedMs = Math.min(
          thinking.remainingMs,
          Math.max(0, Date.now() - thinking.startedAt)
        );
        replyThinkingRef.current = {
          ...thinking,
          elapsedMs: thinking.elapsedMs + elapsedMs,
          startedAt: null,
          remainingMs: thinking.remainingMs - elapsedMs,
          complete: elapsedMs >= thinking.remainingMs,
        };
      }
      cancelReplyPacing();
      if (replyInFlightRef.current) setReplyTyping(false);
      unprocessedF4Ref.current.add(message.id);
      if (analyzeOnSendRef.current) {
        for (const feature of enabledFeaturesRef.current) {
          if (FEATURE_METHODS[feature]) scheduleAnalysis(feature);
        }
      }
      emitMessageSent(message);
      return message;
    },
    [cancelReplyPacing, emitMessageSent, scheduleAnalysis]
  );

  // The sole send operation: append one authored message immediately and
  // resolve with the exact message that was committed. Keeping this
  // Promise-based contract explicit gives all D4 paths one operation while
  // preserving the hook's local, optimistic send semantics.
  const send = useCallback(async (text) => appendSentMessage(text), [appendSentMessage]);

  const previewDraft = useCallback(
    async (text, { trigger = 'manual-preview', attemptId, isCurrent = () => true } = {}) => {
      const scoring = beginDraftScore(trigger, attemptId);
      try {
        const res = await defaultClient.scoreDraft({ conversationId, draft: text }, scoring);
        const spans = res?.spans ?? [];
        settleDraftScore(
          scoring,
          !draftMountedRef.current ? 'discarded' : isCurrent() ? 'preview-returned' : 'stale',
          { spanCount: spans.length }
        );
        return {
          messageInferences: spans.map((span) => ({
            trait: span.inferredTrait,
            confidence: span.confidence,
            value: span.justification ?? '',
          })),
          spans,
        };
      } catch (error) {
        settleDraftScore(scoring, 'failed');
        throw error;
      }
    },
    [beginDraftScore, conversationId, defaultClient, settleDraftScore]
  );

  // A cancellable sleep whose timer is tracked so unmount can clear it. The
  // pacing lives HERE in the hook (never in the client) so the observed chat
  // call's latency_ms stays truthful — observe-client.js times only the client
  // call, so folding these delays into the client would inflate it.
  const sleep = useCallback(
    (ms) =>
      new Promise((resolve) => {
        if (!(ms > 0)) {
          resolve();
          return;
        }
        const id = setTimeout(() => {
          replyTimersRef.current.delete(id);
          resolve();
        }, ms);
        replyTimersRef.current.set(id, resolve);
      }),
    []
  );

  const appendReplyMessage = useCallback((text) => {
    const message = { id: nextId(), role: 'them', text, inferences: [] };
    setHistory((current) => [...current, message]);
    historyRef.current = [...historyRef.current, message];
  }, []);

  const generateReply = useCallback(async () => {
    if (!replyMountedRef.current || replyInFlightRef.current) return null;
    // Select the latest participant target without reordering any delivered
    // bot bubbles in the chronological snapshot.
    const snapshot = [...historyRef.current];
    let targetIndex = -1;
    for (let i = snapshot.length - 1; i >= 0; i -= 1) {
      if (snapshot[i].role === 'me') {
        targetIndex = i;
        break;
      }
    }
    const target = targetIndex === -1 ? null : snapshot[targetIndex];
    // Nothing to answer: no participant message yet (the seeded fixture chat is
    // all 'them'), or the latest one already got its reply.
    if (!target || target.id === lastRepliedMessageIdRef.current) {
      clearReplyScheduleTimers();
      replyFirstPendingAtRef.current = null;
      replyLastPendingAtRef.current = null;
      replyRetryPendingRef.current = false;
      replyThinkReadyAtRef.current = null;
      replyThinkingRef.current = null;
      return null;
    }
    const now = Date.now();
    clearReplyScheduleTimers();
    replyFirstPendingAtRef.current = null;
    replyLastPendingAtRef.current = null;
    replyRetryPendingRef.current = false;
    replyThinkReadyAtRef.current = Math.min(replyThinkReadyAtRef.current ?? now, now);
    replyInFlightRef.current = true;
    const version = replyVersionRef.current;
    const isCurrent = () => replyMountedRef.current && replyVersionRef.current === version;
    // Keep the target in the chronological snapshot. newMessage identifies it
    // by ID; later paced 'them' bubbles remain context, not an implied reorder.
    try {
      const response = await defaultClient.generateReply({
        conversationId,
        history: snapshot,
        newMessage: target,
      });
      if (!isCurrent()) return null;
      // Accept the 3.1.0 { messages } shape, and legacy { text } (from older
      // backends or test mocks) by wrapping it as a single message.
      const messages = Array.isArray(response?.messages)
        ? response.messages
        : typeof response?.text === 'string' && response.text
          ? [{ text: response.text }]
          : [];
      const compute = computeReplyTimingRef.current ?? defaultReplyTiming;
      for (const msg of messages) {
        if (typeof msg?.text !== 'string' || msg.text.length === 0) continue;
        const { thinkingMs, typingMs } = compute(msg);
        const carriedThinking = replyThinkingRef.current;
        const elapsedMs = carriedThinking?.elapsedMs ?? 0;
        const remainingThinkingMs = carriedThinking?.complete
          ? 0
          : Math.max(0, thinkingMs - elapsedMs);
        const thinking = {
          elapsedMs,
          startedAt: remainingThinkingMs > 0 ? Date.now() : null,
          remainingMs: remainingThinkingMs,
          complete: remainingThinkingMs === 0,
        };
        replyThinkingRef.current = thinking;
        await sleep(remainingThinkingMs); // thinking: indicator OFF
        if (!isCurrent()) return null;
        replyThinkingRef.current = {
          elapsedMs: thinking.elapsedMs + remainingThinkingMs,
          startedAt: null,
          remainingMs: 0,
          complete: true,
        };
        setReplyTyping(true);
        await sleep(typingMs); // typing: indicator ON
        if (!isCurrent()) return null;
        appendReplyMessage(msg.text);
        replyThinkingRef.current = null;
        setReplyTyping(false); // brief gap before the next bubble's think phase
      }
      // Only a fully accepted burst protects this target from another trigger.
      // Genuine failures remain retryable; interrupted work is superseded.
      lastRepliedMessageIdRef.current = target.id;
      replyThinkReadyAtRef.current = null;
      replyThinkingRef.current = null;
      return response;
    } catch (error) {
      if (replyMountedRef.current) {
        setReplyTyping(false);
        onSendErrorRef.current?.(error);
      }
      if (isCurrent() && replyFirstPendingAtRef.current === null && !replyRetryPendingRef.current) {
        replyThinkReadyAtRef.current = null;
        replyThinkingRef.current = null;
      }
      return null;
    } finally {
      replyInFlightRef.current = false;
      if (replyMountedRef.current && replyRetryPendingRef.current) {
        fireReplyRef.current?.();
      }
    }
  }, [appendReplyMessage, clearReplyScheduleTimers, conversationId, defaultClient, sleep]);

  // Ref indirection so scheduleReply's setTimeout callbacks and generateReply's
  // retry-on-finish both call the latest fireReply without depending on it in
  // their own dependency arrays (same pattern as scheduleAnalysisRef above).
  const fireReplyRef = useRef(null);
  const fireReply = useCallback(() => {
    clearReplyScheduleTimers();
    if (replyInFlightRef.current) {
      replyRetryPendingRef.current = true;
      return;
    }
    generateReply();
  }, [clearReplyScheduleTimers, generateReply]);
  fireReplyRef.current = fireReply;

  // The public scheduler keeps the initial thinking deadline fixed while
  // requiring the most recent send to remain quiet, subject to one max-wait cap.
  const scheduleReply = useCallback(() => {
    if (!replyMountedRef.current) return;
    const now = Date.now();
    if (replyThinkReadyAtRef.current === null) {
      replyThinkReadyAtRef.current = now + replyDebounceMs;
    }
    const firstAt = replyFirstPendingAtRef.current ?? now;
    replyFirstPendingAtRef.current = firstAt;
    replyLastPendingAtRef.current = now;
    replyRetryPendingRef.current = false;
    clearReplyScheduleTimers();
    const deadline = Math.min(
      Math.max(replyThinkReadyAtRef.current, replyLastPendingAtRef.current + replyQuietMs),
      firstAt + replyMaxWaitMs
    );
    replyDebounceTimerRef.current = setTimeout(
      () => fireReplyRef.current?.(),
      Math.max(0, deadline - now)
    );
    replyMaxWaitTimerRef.current = setTimeout(
      () => fireReplyRef.current?.(),
      Math.max(0, firstAt + replyMaxWaitMs - now)
    );
  }, [clearReplyScheduleTimers, replyDebounceMs, replyMaxWaitMs, replyQuietMs]);

  // Real /draft latency (seconds) regularly outlasts the debounce window
  // (250ms). Without this guard, every post-debounce keystroke pause would
  // fire its own request, stampeding the provider with overlapping in-flight
  // calls. Instead, only one call is ever in flight: a pause that lands while
  // a call is still out coalesces into pendingDraftTextRef and is dispatched
  // — with only the latest text, older pauses are dropped — the moment the
  // in-flight call settles.
  const draftInFlightRef = useRef(false);
  const pendingDraftTextRef = useRef(null);
  const dispatchDraftRef = useRef(null);
  // Assessment and lifecycle describe the latest scoring intent. Scheduling
  // invalidates the previous result immediately, including during debounce
  // and single-flight queuing; late successes and failures cannot restore it.
  const dispatchDraft = useCallback(
    ({ text, scoring, version }) => {
      draftInFlightRef.current = true;
      setDraftAssessment(null);
      setDraftScoring({ text, status: 'pending' });
      defaultClient
        .scoreDraft({ conversationId, draft: text }, scoring)
        .then((response) => {
          const spans = response?.spans ?? [];
          if (!draftMountedRef.current) {
            settleDraftScore(scoring, 'discarded', { spanCount: spans.length });
          } else if (draftVersionRef.current !== version) {
            settleDraftScore(scoring, 'stale', { spanCount: spans.length });
          } else {
            setDraftAssessment({ text, spans, scoreId: scoring.scoreId });
            setDraftScoring({
              text,
              status: spans.length ? 'success' : 'successful-no-findings',
            });
            settleDraftScore(scoring, 'assessment-applied', { spanCount: spans.length });
          }
        })
        .catch((error) => {
          settleDraftScore(scoring, 'failed');
          if (draftMountedRef.current && draftVersionRef.current === version) {
            setDraftScoring({ text, status: 'unavailable' });
            console.warn('[useInference.scoreDraft] dropped', error);
          }
        })
        .finally(() => {
          draftInFlightRef.current = false;
          if (pendingDraftTextRef.current !== null) {
            const next = pendingDraftTextRef.current;
            pendingDraftTextRef.current = null;
            dispatchDraftRef.current?.(next);
          }
        });
    },
    [conversationId, defaultClient, settleDraftScore]
  );
  dispatchDraftRef.current = dispatchDraft;

  const cancelQueuedDraft = useCallback(
    (disposition) => {
      clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
      for (const work of [queuedDraftRef.current, pendingDraftTextRef.current]) {
        if (work) settleDraftScore(work.scoring, disposition);
      }
      queuedDraftRef.current = null;
      pendingDraftTextRef.current = null;
    },
    [settleDraftScore]
  );
  // Cancel queued work and invalidate any assessment response in flight.
  const cancelDraftScoring = useCallback(() => {
    cancelQueuedDraft('cancelled');
    draftVersionRef.current += 1;
    setDraftAssessment(null);
    setDraftScoring({ text: null, status: 'idle' });
  }, [cancelQueuedDraft]);
  const scoreDraft = useCallback(
    (text, { trigger = 'unspecified', attemptId } = {}) => {
      if (!text.trim()) {
        cancelDraftScoring();
        return;
      }
      cancelQueuedDraft('superseded');
      const work = {
        text,
        scoring: beginDraftScore(trigger, attemptId),
        version: ++draftVersionRef.current,
      };
      setDraftAssessment(null);
      setDraftScoring({ text, status: 'pending' });
      queuedDraftRef.current = work;
      draftTimerRef.current = setTimeout(() => {
        queuedDraftRef.current = null;
        draftTimerRef.current = null;
        if (draftInFlightRef.current) {
          pendingDraftTextRef.current = work;
          return;
        }
        dispatchDraftRef.current?.(work);
      }, debounceMs);
    },
    [beginDraftScore, cancelDraftScoring, cancelQueuedDraft, debounceMs]
  );

  useEffect(() => {
    draftMountedRef.current = true;
    replyMountedRef.current = true;
    return () => {
      draftMountedRef.current = false;
      replyMountedRef.current = false;
      replyVersionRef.current += 1;
      replyRetryPendingRef.current = false;
      replyFirstPendingAtRef.current = null;
      replyLastPendingAtRef.current = null;
      replyThinkReadyAtRef.current = null;
      replyThinkingRef.current = null;
      cancelReplyPacing();
      cancelQueuedDraft('cancelled');
      clearReplyScheduleTimers();
    };
  }, [cancelQueuedDraft, cancelReplyPacing, clearReplyScheduleTimers]);

  return {
    send,
    previewDraft,
    generateReply,
    scheduleReply,
    scoreDraft,
    cancelDraftScoring,
    refreshAnalysis,
    latestMeterUpdate,
    latestMirror,
    axisEvidence,
    analysisUpdating,
    analysisSettled,
    analysisErrors,
    draftAssessment,
    draftScoring,
    replyTyping,
    inferenceTriggerTimes,
    history,
    conversationId,
  };
}
